import { CancelPaymentRequestModal } from '@lightspeed/invoicing-library'
import React from 'react'
import ReactDOM from 'react-dom'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { bulkCancelPaymentRequests } from '../../../../functions/invoicing/bulk-cancel-payment-requests'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCustomer,
  selectSaleLocalId,
} from '../../../../state/slices/currentSale/currentSaleSlice'

interface CancelPaymentRequestModalWrapperProps {
  showCancelModal: boolean
  setShowCancelModal: () => void
  numberOfPaymentRequests: number
  totalPaymentRequests: number
  totalToPay: number
  onRefetchPaymentRequests: () => Promise<void>
}

export const CancelPaymentRequestModalWrapper: React.FC<CancelPaymentRequestModalWrapperProps> = ({
  showCancelModal,
  setShowCancelModal,
  numberOfPaymentRequests,
  totalPaymentRequests,
  totalToPay,
  onRefetchPaymentRequests,
}) => {
  const t = useT()
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const customer = useAppSelector(selectCustomer)

  const handleCancelRequests = async () => {
    if (!saleLocalId || !customer?.id) {
      ToastNotifications.negative(
        t('Unable to cancel payment requests', {
          _context: 'Cancel payment requests error',
        })
      )
      return
    }

    const result = await bulkCancelPaymentRequests(saleLocalId, customer.id)

    if (result.success) {
      ToastNotifications.success(
        t(
          '{count, plural, one {Cancelled Pending Payment Request} other {Cancelled Pending Payment Requests}}',
          {
            _context: 'Cancel payment requests success',
            count: numberOfPaymentRequests,
          }
        )
      )
      await onRefetchPaymentRequests() // Refetch to update UI
      setShowCancelModal() // Close modal
    } else {
      ToastNotifications.negative(
        t(result.error || 'Failed to cancel payment requests', {
          _context: 'Cancel payment requests error',
        })
      )
    }
  }

  if (!showCancelModal) return null

  return ReactDOM.createPortal(
    <CancelPaymentRequestModal
      numberOfPaymentRequests={numberOfPaymentRequests}
      totalPaymentRequests={totalPaymentRequests}
      totalToPay={totalToPay}
      onCancel={setShowCancelModal}
      onReturnToSale={setShowCancelModal}
      onCancelRequests={handleCancelRequests}
    />,
    document.body
  )
}
